import { BillingPeriod, CoverageOption, Policy, PolicyLayer } from "@deathstar/types/northstar";
import { NumberFormatter } from "@deathstar/types/NumberFormatter";
import { DateTime } from "@deathstar/types/util";
import { PrinterIcon, ScissorsIcon } from "@heroicons/react/24/outline";
import { Dialog, DialogContent, DialogTitle, IconButton, Typography } from "@material-ui/core";
import React from "react";
import { classNames } from "../classNames/classNames";
import { printElement } from "../printElement/printElement";
import { Context } from "./context";
import NavigatorLogoImgSrc from "./navigator-logo.png";

interface IStatementDialogProps {
    open: boolean;
    onClose: () => void;
    policy: Policy;
    billingPeriod: BillingPeriod;
    layerMetadata: PolicyLayer.Metadata[] | null;
}

export function StatementDialog({ policy, billingPeriod, layerMetadata, open, onClose }: IStatementDialogProps): JSX.Element {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="lg"
            className="relative"
            PaperProps={{ className: "overflow-visible h-full " }}
        >
            <Content policy={policy} billingPeriod={billingPeriod} layerMetadata={layerMetadata} />
        </Dialog>
    );
}

function Content({ policy, billingPeriod }: Pick<IStatementDialogProps, "policy" | "billingPeriod" | "layerMetadata">): JSX.Element {
    const ctx = React.useContext(Context);
    const ref = React.useRef(null);

    const primaryAddress = React.useMemo(() => {
        if (!ctx.properties?.length) return null;
        const primaryMailing = ctx.properties.find((p) => {
            if (p.isDeleted) return false;
            return p.types.some((t) => t.name === "Primary Mailing");
        });
        if (primaryMailing) return primaryMailing;
        const mailing = ctx.properties.find((p) => {
            if (p.isDeleted) return false;
            return p.types.some((t) => t.name === "Mailing");
        });
        if (mailing) return mailing;
        const primaryTerminal = ctx.properties.find((p) => {
            if (p.isDeleted) return false;
            return p.types.some((t) => t.name === "Primary Terminal");
        });
        return primaryTerminal || ctx.properties[0];
    }, [ctx.properties]);
    const totalCharged = React.useMemo(() => {
        return NumberFormatter.LongCurrency.format(billingPeriod.getCharged().amount);
    }, [billingPeriod]);
    const totalDue = React.useMemo(() => {
        return NumberFormatter.LongCurrency.format(billingPeriod.getDue().amount);
    }, [billingPeriod]);
    const statementId = React.useMemo(() => {
        return billingPeriod.statementId;
    }, [billingPeriod.statementId]);
    const { totalPremium, totalPremiumFormatted } = React.useMemo(() => {
        const val = billingPeriod.getTotalPremiumCharge();
        return {
            endorsements: billingPeriod.getEndorsements().map((endo) => ({
                ...endo,
                premium: NumberFormatter.LongCurrency.format(endo.premium),
            })),
            totalPremium: val,
            totalPremiumFormatted: NumberFormatter.LongCurrency.format(val),
        };
    }, [billingPeriod]);
    const payments = React.useMemo(() => {
        return billingPeriod.getMergedChargesAndInvoices().reduce((acc, it) => {
            if (!it.paid || it.paid === 0) return acc;

            const amount = it.paid || 0;
            acc.push({
                invoiceNumber: it.isAmsInvoice ? it.invoiceNumber : `${it.paymentNumber} (PENDING)`,
                amount,
                amountFormatted: NumberFormatter.LongCurrency.format(Math.abs(amount)),
                paymentTypeDescription: it.paymentTypeDescription,
                sign: it.paid! < 0 ? "+" : "-",
            });
            return acc;
        }, [] as { invoiceNumber: string; amountFormatted: string; amount: number; paymentTypeDescription: string; sign: "+" | "-" }[]);
    }, [billingPeriod]);
    const createdDate = React.useMemo(() => new DateTime().format("MM/DD/YYYY HH:MN:SS"), []);
    const customerName = React.useMemo(
        () => ctx.company?.name || policy?.account?.company?.name,
        [ctx.company, policy.account?.company?.name]
    );
    const escrowAmount = React.useMemo(() => {
        if (billingPeriod.index === 0 && policy.billing?.specialPayment.isEscrowDeposit) {
            return {
                amount: policy.billing.specialPayment.amount,
                amountFormatted: NumberFormatter.LongCurrency.format(policy.billing.specialPayment.amount),
            };
        }
        return null;
    }, [billingPeriod.index, policy.billing?.specialPayment.amount, policy.billing?.specialPayment.isEscrowDeposit]);
    const premiumDue = React.useMemo(() => {
        const totalDueAmount = billingPeriod.getDue().amount;
        const totalPaid =
            payments.reduce((tot, pmt) => {
                return tot + (pmt.amount || 0);
            }, 0) - (escrowAmount?.amount || 0);
        if (totalDueAmount === 0) {
            return {
                amount: totalPaid,
                amountFormatted: NumberFormatter.LongCurrency.format(totalPaid),
            };
        }
        if (totalPaid < totalDueAmount && !payments.length) {
            return {
                amount: totalDueAmount,
                amountFormatted: NumberFormatter.LongCurrency.format(totalDueAmount),
            };
        }

        return {
            amount: totalPremium,
            amountFormatted: totalPremiumFormatted,
        };
    }, [billingPeriod, escrowAmount?.amount, payments, totalPremium, totalPremiumFormatted]);

    return (
        <>
            <div className="absolute bottom-0 right-0 justify-end">
                <IconButton
                    className="absolute -right-4 bottom-2 h-16 w-16 translate-x-full flex-col items-center justify-center rounded-lg bg-navigator-primary text-white shadow hover:shadow-xl"
                    onClick={() => {
                        if (!ref.current) return;
                        printElement(ref.current);
                    }}
                >
                    <div className="flex flex-col">
                        <PrinterIcon className="h-8 w-8 shrink-0" />
                        <p className="text-xs small-caps">Print</p>
                    </div>
                </IconButton>
            </div>
            <div ref={ref} className="flex h-full w-full flex-col">
                <DialogTitle>
                    <div className="flex items-center justify-end gap-2">
                        <div className="h-[1px] w-[100px] border border-gray-400"></div>
                        <Typography className="text-right font-mono text-3xl text-gray-400">STATEMENT</Typography>
                        <div className="w-[100px] border border-gray-400"></div>
                    </div>
                </DialogTitle>
                <DialogContent className="flex-1 ">
                    <div className="flex h-full flex-col gap-8">
                        <div className="flex items-start justify-between">
                            <div className="flex flex-col gap-10">
                                <img src={NavigatorLogoImgSrc} alt="Navigator Truck Insurance Logo" className="h-[85px]" />
                                <p className="text-primary font-serif italic">Customer Driven Insurance Solutions&#174;</p>
                            </div>
                            <address className="grid grid-cols-1 gap-2">
                                <div>
                                    <p>Navigator Truck Insurance Agency</p>
                                    <p>529 Baldwin St.</p>
                                    <p>Jenison, MI 49428</p>
                                </div>
                                <div>
                                    <p>Phone: 616.457.7100</p>
                                    <p>Fax: 616.457.7301</p>
                                    <p>Toll-Free: 800.596.TRUCK (8782)</p>
                                </div>
                            </address>
                            <div className="self-end">
                                <table className="w-full table-auto border border-black bg-white">
                                    <tbody>
                                        <tr>
                                            <td className="border border-black bg-gray-200 px-2 py-1 font-bold">Customer</td>
                                            <td className="border border-black px-2 py-1 text-right">{customerName}</td>
                                        </tr>
                                        <tr>
                                            <td className="border border-black bg-gray-200 px-2 py-1 font-bold">Acct #</td>
                                            <td className="border border-black px-2 py-1 text-right">
                                                {ctx.amsClientNumber || policy?.account?.amsId}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="border border-black bg-gray-200 px-2 py-1 font-bold">Due Date</td>
                                            <td className="border border-black px-2 py-1 text-right">
                                                {billingPeriod.dueDate.format("MM/DD/YYYY")}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="flex items-end justify-between">
                            <address>
                                <p>{customerName}</p>
                                <p>{primaryAddress?.street1}</p>
                                {primaryAddress?.street2 && <p>{primaryAddress?.street2}</p>}
                                <p>
                                    {primaryAddress?.city}, {primaryAddress?.state} {primaryAddress?.zip}
                                </p>
                            </address>

                            <table className="table-auto border border-black bg-white">
                                <tbody>
                                    <tr>
                                        <td className="border border-black bg-gray-200 px-2 py-1 font-bold">Policy Number</td>
                                        <td className="border border-black px-2 py-1 text-right">{policy.number}</td>
                                    </tr>
                                    <tr>
                                        <td className="border border-black bg-gray-200 px-2 py-1 font-bold">Amount</td>
                                        <td className="border border-black px-2 py-1 text-right">{totalCharged}</td>
                                    </tr>
                                    <tr>
                                        <td className="border border-black bg-gray-200 px-2 py-1 font-bold">Coverage Dates</td>
                                        <td className="border border-black px-2 py-1 text-right">
                                            {billingPeriod.startDate.format("MM/DD/YYYY")} - {billingPeriod.endDate.format("MM/DD/YYYY")}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="border border-black bg-gray-200 px-2 py-1 font-bold">Statement ID</td>
                                        <td className="border border-black px-2 py-1 text-right">{statementId}</td>
                                    </tr>
                                    <tr className="hidden print:table-row">
                                        <td className="border border-black bg-gray-200 px-2 py-1 font-bold">Statement Created</td>
                                        <td className="border border-black px-2 py-1 text-right">{createdDate}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div className="text-gray-400">
                            <Typography className="text-center">Please detach and return with payment</Typography>
                            <div className="flex items-center">
                                <ScissorsIcon className="h-8 w-8" />
                                <div className="h-1 w-full border-t border-dashed border-gray-400" />
                            </div>
                        </div>

                        <div className="flex flex-1 flex-col print:min-h-[60vh]">
                            <div>
                                <Typography>Customer: {customerName}</Typography>
                            </div>
                            <div className="box-border flex flex-1 flex-col border border-black">
                                <div className="grid w-full grid-cols-[80%_1fr] border-b border-black bg-gray-200 text-center">
                                    <p className="border-r border-black">Description</p>
                                    <p>Amount</p>
                                </div>
                                <Row
                                    description={
                                        <>
                                            <p>{policy.parentCompany?.name}</p>
                                            {policy.parentCompany?.name !== policy.writingCompany?.name && (
                                                <p className="pl-4">/ {policy.writingCompany?.name}</p>
                                            )}
                                        </>
                                    }
                                    amount={premiumDue.amountFormatted}
                                />
                                <Row
                                    description={
                                        <>
                                            {policy.coverages.map((c) => {
                                                const coverageName = CoverageOption.getMetadata(c.coverageOptionId).name;
                                                return <p key={c.coverageOptionId}>{coverageName}</p>;
                                            })}
                                        </>
                                    }
                                />
                                {escrowAmount && <Row description={<p>Escrow Deposit</p>} amount={escrowAmount.amountFormatted} />}
                                <Row
                                    description={
                                        <>
                                            <p>Policy Effective Dates</p>
                                            <p className="pl-4">
                                                {new DateTime(policy.effectiveDate).format("MM/DD/YY")} -{" "}
                                                {new DateTime(policy.expirationDate).format("MM/DD/YY")}
                                            </p>
                                        </>
                                    }
                                />
                                {/* <Row
                                    description={
                                        <>
                                            <p>Endorsements:</p>

                                            {!endorsements?.length && <p className="pl-4">None</p>}

                                            {endorsements && endorsements.length > 0 && (
                                                <ul className="pl-4">
                                                    {endorsements?.map((endo) => {
                                                        return (
                                                            <li key={endo.id}>
                                                                #{endo.invoiceNumber} - {endo.effectiveDate.format("MM/DD/YY")} -{" "}
                                                                {endo.description.slice(0, 90)}
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            )}
                                        </>
                                    }
                                    amount={
                                        <div className="self-end">
                                            {endorsements && endorsements.length > 0 && (
                                                <ul className="">
                                                    {endorsements?.map((endo) => {
                                                        return (
                                                            <li className="pr-2 text-right" key={endo.id}>
                                                                {endo.premium}
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            )}
                                        </div>
                                    }
                                /> */}
                                <Row
                                    description={
                                        <>
                                            <p>Payments:</p>

                                            {!payments?.length && <p className="pl-4">None</p>}

                                            {payments && payments.length > 0 && (
                                                <ul className="pl-4">
                                                    {payments?.map((payment) => {
                                                        return (
                                                            <li key={payment.invoiceNumber}>
                                                                <span>#{payment.invoiceNumber}</span>
                                                                {payment.paymentTypeDescription && (
                                                                    <span className=""> ({payment.paymentTypeDescription})</span>
                                                                )}
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            )}
                                        </>
                                    }
                                    amount={
                                        <div className="self-end">
                                            {payments && payments.length > 0 && (
                                                <ul className="">
                                                    {payments?.map((payment) => {
                                                        return (
                                                            <li className="pr-2 text-right" key={payment.invoiceNumber}>
                                                                {payment.sign}
                                                                {payment.amountFormatted}
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            )}
                                        </div>
                                    }
                                />

                                <Row className="h-full flex-1" description={<div className="invisible"></div>} />
                            </div>
                            <div className="flex">
                                <div className="invisible flex-1 justify-self-start print:visible ">
                                    <p>Statement created: {createdDate}</p>
                                </div>

                                <div className="w-[calc(20%+2px)] self-end">
                                    <div className="box-border border border-t-0 border-black">
                                        <p className="box-border border-b border-black bg-gray-200 text-center">Due</p>
                                        <p className="p-2 text-right">{totalDue}</p>
                                    </div>
                                    <p className="pt-2 text-center text-gray-400">Thank You</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </div>
        </>
    );
}

function Row({
    description,
    amount,
    className,
}: {
    description: JSX.Element;
    amount?: JSX.Element | number | string;
    className?: string;
}): JSX.Element {
    return (
        <div className={classNames("grid w-full grid-cols-[80%_1fr]", className)}>
            <div className="border-r border-black pl-2 pt-6">{description}</div>
            {(typeof amount === "string" || typeof amount === "number") && <p className=" px-2 py-1 pt-6 text-right">{amount}</p>}
            {typeof amount === "object" && amount}
        </div>
    );
}
